import { referencedTasksFor, type AnswerRefAlias } from '@agavi/blocks';

import type { Block, Task } from '@/types/experience';

/**
 * WHAT A PASTE HAS TO OFFER TO BRING.
 *
 * A chip stores an alias id, the alias names an address in some task, and pasting the chip into
 * an experience with no such task leaves a reference that can never resolve. Carrying the alias
 * makes that legible; carrying the TASK is what makes it work.
 *
 * It runs in the CLIENT, which can fetch the source experience it already owns. The server was
 * the other candidate and would have had to read a second document; this way the paste endpoint
 * keeps its property of only ever touching the one it was called on.
 */
export interface PullRow {
  taskId: string;
  title: string;
  /** Titles of the tasks that cite it, for the row's "why". */
  citedBy: string[];
  /** Hops from the copied set. 0 is cited by the copy itself. */
  depth: number;
  /** At least one citation is a driven version axis rather than a chip — see `PullCandidate`. */
  drives: boolean;
}

/**
 * The id of the stand-in task a BLOCK paste walks from.
 *
 * A block copy has no task to seed the walk with, so the blocks are wrapped in one. The same
 * trick `aliasesNamedBy` uses, and for the same reason: one chip census rather than two.
 */
export const PASTED_BLOCKS_SEED = '__pasted-blocks__';

export function seedTaskForBlocks(blocks: Block[]): Task {
  // Named after itself ON PURPOSE. The stand-in must never reach a dialog, and a blank title
  // would leak as an empty bullet that nothing notices; this way a leak is the marker, visible
  // on screen and assertable in a test.
  return ({ id: PASTED_BLOCKS_SEED, title: PASTED_BLOCKS_SEED, blocks } as unknown) as Task;
}

/**
 * WHAT THE DESTINATION ALREADY HAS, in the only terms the source can be compared against.
 *
 * A pulled task keeps nothing of its source id — it is minted fresh — so the destination's own
 * ids say nothing about what has already been brought. What identifies it is `heritageId`, the
 * task it was copied FROM, which is exactly the source id the walk is working in.
 *
 * Without it, pasting the same block twice offers the same task again every time: the first
 * pull would be invisible and the teacher would collect duplicates. Reported as "when I pasted
 * the same block again, it asked to reimport the task again".
 */
export function alreadyHereIds(destinationTasks: Task[]): Set<string> {
  const out = new Set<string>();
  for (const t of destinationTasks) {
    out.add(t.id);
    if (t.heritageId) out.add(t.heritageId);
  }
  return out;
}

export function pullRows(args: {
  /** The SOURCE experience's tasks, including any stand-in seed. */
  sourceTasks: Task[];
  sourceAliases: AnswerRefAlias[];
  seedTaskIds: string[];
  /** Ids the destination already holds, its own AND its copies' — see `alreadyHereIds`. */
  alreadyHere: Set<string>;
}): PullRow[] {
  const { sourceTasks, sourceAliases, seedTaskIds, alreadyHere } = args;
  const titleOf = (id: string) => sourceTasks.find(t => t.id === id)?.title ?? '';

  return referencedTasksFor(
    { tasks: sourceTasks as never, aliases: sourceAliases },
    seedTaskIds,
  )
    // A same-document paste finds everything already present and offers nothing, which is
    // right: the answer is in this experience, so the reference resolves without help.
    .filter(c => !alreadyHere.has(c.taskId))
    .map(c => ({
      taskId: c.taskId,
      title: titleOf(c.taskId),
      // The stand-in is never shown by name. "Cited by the thing you are pasting" is not a
      // fact a teacher needs spelled out, and it has no title to spell.
      citedBy: c.citedBy.filter(id => id !== PASTED_BLOCKS_SEED).map(titleOf).filter(Boolean),
      depth: c.depth,
      drives: c.drives,
    }))
    // Shallowest first: what the copy itself cites reads before what those drag in.
    .sort((a, b) => a.depth - b.depth);
}

/**
 * THE ORDER PULLED TASKS ARE PASTED IN, which is the REVERSE of how they are listed.
 *
 * The list reads outward from the paste — "this needs Setup, and Setup needs Field work" —
 * because that is the order the reasoning runs in. The sequence has to run the other way: a
 * reference resolves only if the answer is reached BEFORE the task citing it, so the deepest
 * dependency goes furthest up the lane. Pasted in list order, Setup would land ahead of the
 * Field work it depends on and arrive broken, having been brought along precisely so it would
 * not.
 *
 * Separate and pure because the two orders look interchangeable and are not, and because
 * nothing on screen shows the difference — the mistake would only surface as a reference that
 * is still red after a pull that said it had fixed it.
 */
export function pullPasteOrder(rows: PullRow[], chosen: Iterable<string>): string[] {
  const keep = new Set(chosen);
  return rows
    // `filter` already hands back a new array, so the sort below cannot reorder the caller's
    // list. No defensive `slice()`: one that can never change the answer is a line no test
    // can hold down.
    .filter(r => keep.has(r.taskId))
    .sort((a, b) => b.depth - a.depth)
    .map(r => r.taskId);
}
